import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'

function ComponentFunnel ({
  seriesId,
  data,
  name,
  min,
  max,
  minSize,
  maxSize,
  orient,
  sort,
  gap,
  funnelAlign,
  label,
  labelLine,
  itemStyle,
  emphasis,
  blur,
  select,
  selectedMode,
  width,
  height,
  left,
  top,
  right,
  bottom,
  zlevel,
  z,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationDelayUpdate,
  animationEasingUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, {
      type: 'funnel',
      id: seriesId,
      data,
      name,
      min,
      max,
      minSize,
      maxSize,
      orient,
      sort,
      gap,
      funnelAlign,
      label,
      labelLine,
      itemStyle,
      emphasis,
      blur,
      select,
      selectedMode,
      width,
      height,
      left,
      top,
      right,
      bottom,
      zlevel,
      z,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationDelayUpdate,
      animationEasingUpdate,
      universalTransition,
      tooltip
    })
  }, [
    seriesId,
    data,
    name,
    min,
    max,
    minSize,
    maxSize,
    orient,
    sort,
    gap,
    funnelAlign,
    label,
    labelLine,
    itemStyle,
    emphasis,
    blur,
    select,
    selectedMode,
    width,
    height,
    left,
    top,
    right,
    bottom,
    zlevel,
    z,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationDelayUpdate,
    animationEasingUpdate,
    universalTransition,
    tooltip,
    setSeries
  ])

  return null
}

ComponentFunnel.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.array,

  /** The specified minimum value. */
  min: PropTypes.number,

  /** The specified maximum value. */
  max: PropTypes.number,

  /** The mapped width of data minimum value.
   It can be absolute pixel and also the percentage of chart width which is useful when you want the funnel to be based on both sides. */
  minSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The mapped width of data maximum value.
   It can be absolute pixel and also the percentage of chart width which is useful when you want the funnel to be based on both sides. */
  maxSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Orientation of the funnel. Can be 'vertical' or 'horizontal'. */
  orient: PropTypes.oneOf(['vertical', 'horizontal']),

  /** Data sorting, which can be 'ascending', 'descending', 'none' (in data order), or a function. */
  sort: PropTypes.oneOfType([
    PropTypes.oneOf(['ascending', 'descending', 'none']),
    PropTypes.func
  ]),

  /** Gap between each trapezoid in a funnel chart. */
  gap: PropTypes.number,

  /** Horizontal align of funnel. Options: 'left', 'center', 'right'. */
  funnelAlign: PropTypes.oneOf(['left', 'center', 'right']),

  /** Text label of graphic element, to explain some data information about graphic item like value, name and so on. */
  label: PropTypes.object,

  /** Configuration of label guide line. */
  labelLine: PropTypes.object,

  /** Graphic style of the funnel pieces. */
  itemStyle: PropTypes.object,

  /** Highlight style of the graphic. */
  emphasis: PropTypes.object,

  /** Configurations of blur state. Available when emphasis.focus is set. */
  blur: PropTypes.object,

  /** Configurations of select state. Available when selectedMode is set. */
  select: PropTypes.object,

  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.
   Besides, it can be set to 'single', 'multiple', for single selection, multiple selections. */
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['single', 'multiple'])]),

  /** Width of funnel. Can be absolute pixel or percentage. */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Height of funnel. Can be absolute pixel or percentage. */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between funnel component and the left side of the container. */
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between funnel component and the top side of the container. */
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between funnel component and the right side of the container. */
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between funnel component and the bottom side of the container. */
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** zlevel value of all graphical elements in funnel. */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in funnel, which controls order of drawing graphical components. */
  z: PropTypes.number,

  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. */
  silent: PropTypes.bool,

  /** Whether to enable animation. */
  animation: PropTypes.bool,

  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. */
  animationThreshold: PropTypes.number,

  /** Duration of the first animation. */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation. */
  animationDelay: PropTypes.number,

  /** Time for animation to complete. */
  animationDurationUpdate: PropTypes.number,

  /** Delay before updating animation. */
  animationDelayUpdate: PropTypes.number,

  /** Easing method used for animation. */
  animationEasingUpdate: PropTypes.string,

  universalTransition: PropTypes.object,

  tooltip: PropTypes.object
}

ComponentFunnel.defaultProps = {
  orient: 'vertical',
  sort: 'descending',
  gap: 0,
  funnelAlign: 'center'
}

export default ComponentFunnel
